import { resolveSectionQuestions } from '@/features/rules/fact-key-palette'
import { evaluateLogic, kotlinToDoubleOrNull, type Facts } from '@/features/rules/logic-validate'
import { CategoryIndex } from '@/lib/category-ref'
import { COST_TYPES } from '@/lib/constants'
import type {
  ConfigQuestion,
  CostType,
  Currency,
  Material,
  MaterialCategory,
  MaterialEffect,
  MaterialPrice,
  QuestionGroup,
  QuestionHint,
  QuestionOption,
  SectionType,
} from '@/lib/types'

/**
 * "Симулировать калькуляцию": the app's automated calculation for ONE room of a section type, run in
 * the browser against answers typed into the console. Nothing is stored.
 *
 * Ports (keep in sync with the Kotlin sources):
 * - `ComputeAutomatedMaterialsUseCase` (`AutomatedMaterials.kt`) — which effects run and which
 *   material each one targets, `{attr:CODE}` seeding, MULTI_CHOICE / REPEATER / QUESTION_LIST rules;
 * - `QuestionVisibility` (fail-open) and `HintVisibility` (fail-closed);
 * - `RoomViewModel` — how each answer type is stored as facts, CHOICE options;
 * - `MultiChoiceAnswer` / `RepeaterAnswer` JSON codecs;
 * - `BuildCombinedEstimateUseCase` pricing of interview lines: catalog price × quantity (project
 *   price overrides, labor, discount and tax are project settings and do not exist here).
 */

// ---- Answer codecs (`MultiChoiceAnswer.kt`, `RepeaterAnswer.kt`) ----

export type MultiChoiceItem = { materialId: string; count: number; price: number }
export type RepeaterEntry = { values: Record<string, string> }

/** Total decode like kotlinx (`ignoreUnknownKeys`): any malformed element → empty list. */
export function decodeMultiChoice(raw: string): MultiChoiceItem[] {
  if (!raw.trim()) return []
  try {
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    const items: MultiChoiceItem[] = []
    for (const element of parsed) {
      if (typeof element !== 'object' || element === null) return []
      const { materialId, count, price } = element as Record<string, unknown>
      if (typeof materialId !== 'string' || typeof count !== 'number') return []
      if (price !== undefined && typeof price !== 'number') return []
      items.push({ materialId, count, price: price ?? 0 })
    }
    return items
  } catch {
    return []
  }
}

export function encodeMultiChoice(items: MultiChoiceItem[]): string {
  return items.length === 0 ? '' : JSON.stringify(items)
}

export function decodeRepeater(raw: string): RepeaterEntry[] {
  if (!raw.trim()) return []
  try {
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    const entries: RepeaterEntry[] = []
    for (const element of parsed) {
      if (typeof element !== 'object' || element === null) return []
      const values = (element as Record<string, unknown>).values ?? {}
      if (typeof values !== 'object' || values === null || Array.isArray(values)) return []
      const record: Record<string, string> = {}
      for (const [key, value] of Object.entries(values)) {
        if (typeof value !== 'string') return []
        record[key] = value
      }
      entries.push({ values: record })
    }
    return entries
  } catch {
    return []
  }
}

export function encodeRepeater(entries: RepeaterEntry[]): string {
  return entries.length === 0 ? '' : JSON.stringify(entries)
}

// ---- Answers typed in the simulation → room facts (as `RoomViewModel` stores them) ----

/**
 * The raw state of the simulation form. Everything is keyed by the fact key the answer is stored
 * under, as in the app (two questions sharing a key share one fact).
 */
export type SimulationAnswers = {
  /** NUMBER, TEXT, BOOLEAN ("true"/"false"), CHOICE (material id), SELECT (option value). */
  scalar: Record<string, string>
  /** KEY_SET item key → checked. */
  keySet: Record<string, boolean>
  /** MULTI_CHOICE: material id → count as typed; a present key means "selected". */
  multi: Record<string, Record<string, string>>
  /** REPEATER / QUESTION_LIST: the entries as typed (blank values included). */
  repeater: Record<string, Array<Record<string, string>>>
}

export const EMPTY_ANSWERS: SimulationAnswers = { scalar: {}, keySet: {}, multi: {}, repeater: {} }

/** Count text → number like the app's MULTI_CHOICE input (`trim`, `,` → `.`, `toDoubleOrNull`). */
export function parseCount(text: string): number {
  return kotlinToDoubleOrNull(text.trim().replace(',', '.')) ?? 0
}

/**
 * The fact map the app would hold for these answers:
 * - a scalar answer is trimmed, blank = no fact (`onAnswer`);
 * - a checked KEY_SET item writes "true" under its own trimmed key, unchecked = no fact;
 * - MULTI_CHOICE keeps selections with a positive count, as JSON (`onMultiChoiceChange`);
 * - REPEATER drops blank values and entries left empty, as JSON (`onRepeaterChange`); for the
 *   fact keys in `singleEntryKeys` (QUESTION_LIST questions) only the first entry is kept.
 */
export function buildFacts(
  answers: SimulationAnswers,
  singleEntryKeys?: ReadonlySet<string>,
): Record<string, string> {
  const facts: Record<string, string> = {}
  for (const [key, raw] of Object.entries(answers.scalar)) {
    const value = raw.trim()
    if (key && value) facts[key] = value
  }
  for (const [rawKey, checked] of Object.entries(answers.keySet)) {
    const key = rawKey.trim()
    if (key && checked) facts[key] = 'true'
  }
  for (const [key, selections] of Object.entries(answers.multi)) {
    const items = Object.entries(selections)
      .map(([materialId, count]) => ({ materialId, count: parseCount(count), price: 0 }))
      .filter((item) => item.materialId.trim() !== '' && item.count > 0)
    const encoded = encodeMultiChoice(items)
    if (key && encoded) facts[key] = encoded
  }
  for (const [key, entries] of Object.entries(answers.repeater)) {
    const clean = entries
      .map((values) => ({
        values: Object.fromEntries(Object.entries(values).filter(([, value]) => value.trim() !== '')),
      }))
      .filter((entry) => Object.keys(entry.values).length > 0)
    // QUESTION_LIST holds exactly one entry (`onRepeaterChange` → take(1)).
    const kept = singleEntryKeys?.has(key) ? clean.slice(0, 1) : clean
    const encoded = encodeRepeater(kept)
    if (key && encoded) facts[key] = encoded
  }
  return facts
}

// ---- Static context of one section type ----

export type SimulationConfig = {
  sectionType: SectionType
  questions: ConfigQuestion[]
  groups: QuestionGroup[]
  options: QuestionOption[]
  effects: MaterialEffect[]
  hints: QuestionHint[]
  materials: Material[]
  /** The category catalog: references resolve by id, else by name (`CategoryIndex`). */
  categories: MaterialCategory[]
}

export type SimulationContext = {
  /** The room's questions in interview order (`SectionQuestionResolver.resolve`). */
  questions: ConfigQuestion[]
  optionsByQuestion: Map<string, QuestionOption[]>
  effectsByQuestion: Map<string, MaterialEffect[]>
  /** Hints of the applicable groups, by `orderIndex` (the room's hint order). */
  hints: QuestionHint[]
  materialById: Map<string, Material>
  groupById: Map<string, QuestionGroup>
  /**
   * Materials offered for a category reference (id, else name), sorted by name — as `RoomViewModel`
   * (`CategoryIndex.groupMaterials`). A blank reference offers nothing.
   */
  materialsOf: (categoryId: string, categoryName: string) => Material[]
  /** A category reference's current name (a renamed category shows its new name). */
  categoryName: (categoryId: string, categoryName: string) => string
  /** Fact keys of the room's QUESTION_LIST questions (one entry only; see `buildFacts`). */
  singleEntryKeys: ReadonlySet<string>
}

export function buildSimulationContext(config: SimulationConfig): SimulationContext {
  const questions = resolveSectionQuestions(
    config.sectionType,
    config.sectionType.id,
    config.questions,
    config.groups,
  )
  const optionsByQuestion = new Map<string, QuestionOption[]>()
  for (const option of config.options) {
    const list = optionsByQuestion.get(option.questionId) ?? []
    list.push(option)
    optionsByQuestion.set(option.questionId, list)
  }
  for (const list of optionsByQuestion.values()) list.sort((a, b) => a.orderIndex - b.orderIndex)
  const effectsByQuestion = new Map<string, MaterialEffect[]>()
  for (const effect of config.effects) {
    const list = effectsByQuestion.get(effect.questionId) ?? []
    list.push(effect)
    effectsByQuestion.set(effect.questionId, list)
  }
  const selected = new Set(config.sectionType.groupIds)
  const applicable = new Set(
    config.groups.filter((group) => group.isGlobal || selected.has(group.id)).map((group) => group.id),
  )
  const hints = config.hints
    .filter((hint) => applicable.has(hint.groupId))
    .sort((a, b) => a.orderIndex - b.orderIndex)
  // `observeMaterials` sorts the catalog by lower-cased name; inactive items are included too.
  const sorted = [...config.materials].sort((a, b) => {
    const left = a.name.toLowerCase()
    const right = b.name.toLowerCase()
    return left < right ? -1 : left > right ? 1 : 0
  })
  const categoryIndex = new CategoryIndex(config.categories)
  const byCategory = categoryIndex.groupMaterials(sorted)
  return {
    questions,
    optionsByQuestion,
    effectsByQuestion,
    hints,
    materialById: new Map(config.materials.map((material) => [material.id, material])),
    groupById: new Map(config.groups.map((group) => [group.id, group])),
    materialsOf: (categoryId, categoryName) =>
      byCategory.get(categoryIndex.key(categoryId, categoryName)) ?? [],
    categoryName: (categoryId, categoryName) => categoryIndex.displayName(categoryId, categoryName),
    singleEntryKeys: new Set(
      questions.filter((question) => question.answerType === 'QUESTION_LIST').map((question) => question.factKey),
    ),
  }
}

/** `hasFieldSchema` in the app. */
export const hasFieldSchema = (question: ConfigQuestion) =>
  question.answerType === 'REPEATER' || question.answerType === 'QUESTION_LIST'

// ---- The calculation ----

export type QuestionState = {
  question: ConfigQuestion
  visible: boolean
  /** The display condition does not evaluate: the app shows the question anyway (fail-open). */
  conditionError: string | null
}

/** One evaluation of one effect, for the "how it was calculated" table. */
export type TraceRow = {
  questionId: string
  effectId: string | null
  /** 1-based position of the effect among the question's effects; `null` = no effect involved. */
  effectNumber: number | null
  /** 1-based REPEATER entry; `null` for other types. */
  entryNumber: number | null
  /** Material the result goes to ('' = none). */
  target: string
  /** The expression's value (0 on an error, as the engine does). */
  value: number
  /** MULTI_CHOICE: the result is multiplied by the item's count. */
  multiplier: number | null
  error: string | null
  /** Why nothing was added (`null` when the row adds `value × multiplier`). */
  skipped: string | null
}

export type SimulationResult = {
  questions: QuestionState[]
  /** Aggregated quantities in the engine's insertion order. */
  quantities: Array<{ materialId: string; quantity: number }>
  trace: TraceRow[]
  /** Hints whose condition is true now, in order (the app shows them one at a time). */
  hints: QuestionHint[]
  hintErrors: Array<{ hint: QuestionHint; error: string }>
  /** Materials whose attribute values `{attr:CODE}` may need (the engine's pre-collection pass). */
  attributeMaterialIds: string[]
}

/** True / false / error for a display condition, as `QuestionVisibility.isVisible`. */
function visibility(question: ConfigQuestion, facts: Facts): { visible: boolean; error: string | null } {
  const condition = question.displayCondition.trim()
  if (!condition) return { visible: true, error: null }
  const result = evaluateLogic(condition, facts)
  return result.ok ? { visible: result.value !== 0, error: null } : { visible: true, error: result.error }
}

function withAttributes(facts: Facts, attributes: Record<string, string> | undefined): Facts {
  if (!attributes || Object.keys(attributes).length === 0) return facts
  const merged: Record<string, string> = { ...facts }
  for (const [code, value] of Object.entries(attributes)) merged[`attr:${code}`] = value
  return merged
}

/**
 * Runs the room calculation. `attributes` holds attribute values by material id → code → value; a
 * material that is not loaded yet simply has no `{attr:…}` facts for now.
 */
export function simulate(
  ctx: SimulationContext,
  facts: Facts,
  attributes: ReadonlyMap<string, Record<string, string>>,
): SimulationResult {
  const questions = ctx.questions.map((question) => {
    const state = visibility(question, facts)
    return { question, visible: state.visible, conditionError: state.error }
  })
  const visible = questions.filter((item) => item.visible).map((item) => item.question)
  const fact = (key: string) => (Object.prototype.hasOwnProperty.call(facts, key) ? (facts[key] ?? '') : '')
  const effectsOf = (question: ConfigQuestion) => ctx.effectsByQuestion.get(question.id) ?? []
  const materialFieldKeys = (question: ConfigQuestion) =>
    (ctx.optionsByQuestion.get(question.id) ?? [])
      // A CHOICE field without a key stores no pick: not a material field (as in the engine).
      .filter((option) => option.fieldType === 'CHOICE' && option.value.trim() !== '')
      .map((option) => option.value)

  // Pass 1 — the materials whose attributes may be read (same collection as the engine).
  const attributeIds = new Set<string>()
  for (const question of visible) {
    if (question.answerType === 'CHOICE') {
      const id = fact(question.factKey).trim()
      if (id) attributeIds.add(id)
    } else if (question.answerType === 'MULTI_CHOICE' && effectsOf(question).length > 0) {
      for (const item of decodeMultiChoice(fact(question.factKey))) {
        if (item.materialId.trim()) attributeIds.add(item.materialId)
      }
    } else if (hasFieldSchema(question) && effectsOf(question).length > 0) {
      const keys = materialFieldKeys(question).filter((key) => key.trim())
      for (const entry of decodeRepeater(fact(question.factKey))) {
        for (const key of keys) {
          const value = entry.values[key]
          if (value && value.trim()) attributeIds.add(value)
        }
      }
    }
  }

  // Pass 2 — the quantities.
  const totals = new Map<string, number>()
  const add = (materialId: string, quantity: number) =>
    totals.set(materialId, (totals.get(materialId) ?? 0) + quantity)
  const trace: TraceRow[] = []
  const run = (expression: string, evalFacts: Facts) => {
    const result = evaluateLogic(expression, evalFacts)
    return result.ok ? { value: result.value, error: null } : { value: 0, error: result.error }
  }

  for (const question of visible) {
    const effects = effectsOf(question)

    if (question.answerType === 'MULTI_CHOICE') {
      const items = decodeMultiChoice(fact(question.factKey))
      // Fixed cost-item effects ride along with every selected item (result × its count); they do
      // not decide how the selected items themselves are counted.
      const fixedEffects = effects.filter((effect) => effect.fixedMaterial)
      const pickedEffects = effects.filter((effect) => !effect.fixedMaterial)
      const addFixed = (item: { materialId: string; count: number }) => {
        if (item.materialId.trim() === '' || item.count === 0) return
        const evalFacts = withAttributes(facts, attributes.get(item.materialId))
        for (const effect of fixedEffects) {
          const { value, error } = run(effect.expression, evalFacts)
          const quantity = value * item.count
          const skipped =
            effect.materialId.trim() === '' ? 'пункт эффекта не выбран' : quantity === 0 ? 'результат × количество = 0' : null
          if (skipped === null) add(effect.materialId, quantity)
          trace.push({
            questionId: question.id,
            effectId: effect.id,
            effectNumber: effects.indexOf(effect) + 1,
            entryNumber: null,
            target: effect.materialId,
            value,
            multiplier: item.count,
            error,
            skipped,
          })
        }
      }
      if (pickedEffects.length === 0) {
        // No effect: the entered count IS the quantity.
        for (const item of items) {
          const ok = item.materialId.trim() !== '' && item.count !== 0
          if (ok) add(item.materialId, item.count)
          trace.push({
            questionId: question.id,
            effectId: null,
            effectNumber: null,
            entryNumber: null,
            target: item.materialId,
            value: item.count,
            multiplier: null,
            error: null,
            skipped: ok ? null : 'нет пункта или количества',
          })
          addFixed(item)
        }
      } else {
        for (const item of items) {
          if (item.materialId.trim() === '' || item.count === 0) {
            trace.push({
              questionId: question.id,
              effectId: null,
              effectNumber: null,
              entryNumber: null,
              target: item.materialId,
              value: 0,
              multiplier: item.count,
              error: null,
              skipped: 'нет пункта или количества',
            })
            continue
          }
          const evalFacts = withAttributes(facts, attributes.get(item.materialId))
          let perUnit = 0
          // The engine sums every effect per item and adds (sum × count) once, so a row is "not
          // counted" only when that product is 0 — an effect returning 0 still takes part.
          const rows = pickedEffects.map((effect) => {
            const { value, error } = run(effect.expression, evalFacts)
            perUnit += value
            return {
              questionId: question.id,
              effectId: effect.id,
              effectNumber: effects.indexOf(effect) + 1,
              entryNumber: null,
              target: item.materialId,
              value,
              multiplier: item.count,
              error,
            }
          })
          const quantity = perUnit * item.count
          const skipped = quantity === 0 ? 'сумма эффектов × количество = 0' : null
          trace.push(...rows.map((row): TraceRow => ({ ...row, skipped })))
          if (quantity !== 0) add(item.materialId, quantity)
          addFixed(item)
        }
      }
      continue
    }

    if (hasFieldSchema(question)) {
      if (effects.length === 0) continue
      const fieldKeys = materialFieldKeys(question)
      const decoded = decodeRepeater(fact(question.factKey))
      const entries = question.answerType === 'QUESTION_LIST' ? decoded.slice(0, 1) : decoded
      entries.forEach((entry, entryIndex) => {
        const entryFacts: Facts = { ...facts, ...entry.values }
        effects.forEach((effect, index) => {
          const entryNumber = question.answerType === 'QUESTION_LIST' ? null : entryIndex + 1
          // A fixed cost-item effect names no field: once per entry in which ANY material field has a
          // pick, its own item, no `{attr:}` (as in the engine).
          if (effect.fixedMaterial && fieldKeys.length > 0) {
            const anyPick = fieldKeys.some((key) => (entry.values[key] ?? '').trim() !== '')
            const { value, error } = run(effect.expression, entryFacts)
            const skipped = !anyPick
              ? 'в записи не выбран ни один материал'
              : effect.materialId.trim() === ''
                ? 'пункт эффекта не выбран'
                : value === 0
                  ? 'результат 0'
                  : null
            if (skipped === null) add(effect.materialId, value)
            trace.push({
              questionId: question.id,
              effectId: effect.id,
              effectNumber: index + 1,
              entryNumber,
              target: effect.materialId,
              value,
              multiplier: null,
              error,
              skipped,
            })
            return
          }
          const fieldKey =
            effect.targetFieldKey.trim() !== '' && fieldKeys.includes(effect.targetFieldKey)
              ? effect.targetFieldKey
              : (fieldKeys[0] ?? '')
          const target = fieldKey.trim() !== '' ? (entry.values[fieldKey] ?? '') : effect.materialId
          const attrs = fieldKey.trim() !== '' && target.trim() !== '' ? attributes.get(target) : undefined
          const { value, error } = run(effect.expression, withAttributes(entryFacts, attrs))
          const skipped =
            value === 0
              ? 'результат 0'
              : target.trim() === ''
                ? fieldKey.trim() !== ''
                  ? `в поле «${fieldKey}» пункт не выбран`
                  : 'пункт эффекта не выбран'
                : null
          if (skipped === null) add(target, value)
          trace.push({
            questionId: question.id,
            effectId: effect.id,
            effectNumber: index + 1,
            entryNumber,
            target,
            value,
            multiplier: null,
            error,
            skipped,
          })
        })
      })
      continue
    }

    if (effects.length === 0) continue
    // Every other type — including FACT (effects only, never shown to the user) — targets each
    // effect's fixed item, evaluated against the room's facts (as in `AutomatedMaterials.kt`).
    const isChoice = question.answerType === 'CHOICE'
    const selected = isChoice ? fact(question.factKey).trim() : ''
    const evalFacts = withAttributes(facts, selected ? attributes.get(selected) : undefined)
    effects.forEach((effect, index) => {
      const { value, error } = run(effect.expression, evalFacts)
      // A fixed cost-item effect adds its own item, only once a material is picked.
      const fixedCompanion = isChoice && effect.fixedMaterial
      const target = isChoice && !fixedCompanion ? selected : effect.materialId
      const skipped =
        fixedCompanion && selected === ''
          ? 'пункт в ответе не выбран'
          : value === 0
            ? 'результат 0'
            : target.trim() === ''
              ? isChoice && !fixedCompanion
                ? 'пункт в ответе не выбран'
                : 'пункт эффекта не выбран'
              : null
      if (skipped === null) add(target, value)
      trace.push({
        questionId: question.id,
        effectId: effect.id,
        effectNumber: index + 1,
        entryNumber: null,
        target,
        value,
        multiplier: null,
        error,
        skipped,
      })
    })
  }

  // Hints (`HintVisibility.isTriggered`: blank or broken condition = not shown).
  const hints: QuestionHint[] = []
  const hintErrors: Array<{ hint: QuestionHint; error: string }> = []
  for (const hint of ctx.hints) {
    const condition = hint.condition.trim()
    if (!condition) continue
    const result = evaluateLogic(condition, facts)
    if (!result.ok) hintErrors.push({ hint, error: result.error })
    else if (result.value !== 0) hints.push(hint)
  }

  return {
    questions,
    quantities: [...totals.entries()].map(([materialId, quantity]) => ({ materialId, quantity })),
    trace,
    hints,
    hintErrors,
    attributeMaterialIds: [...attributeIds].sort(),
  }
}

// ---- Pricing (`BuildCombinedEstimateUseCase`, interview lines) ----

export type PricedLine = {
  materialId: string
  name: string
  unit: string
  costType: CostType
  quantity: number
  unitPrice: number
  currency: Currency | null
  lineTotal: number
  /** The material is not in the catalog visible to this account (deleted, or someone's private). */
  isUnknown: boolean
  hasPrice: boolean
}

export type PricedEstimate = {
  lines: PricedLine[]
  subtotals: Array<{ costType: CostType; amount: number }>
  total: number
  /** Distinct currencies of the priced lines; the app adds amounts without conversion. */
  currencies: Currency[]
}

export function priceQuantities(
  quantities: SimulationResult['quantities'],
  materialById: ReadonlyMap<string, Material>,
  prices: ReadonlyMap<string, MaterialPrice>,
): PricedEstimate {
  const lines = quantities.map(({ materialId, quantity }) => {
    const material = materialById.get(materialId)
    const price = prices.get(materialId)
    const unitPrice = price?.amount ?? 0
    return {
      materialId,
      name: material?.name ?? materialId,
      unit: material?.unit ?? '',
      costType: material?.costType ?? 'MATERIAL',
      quantity,
      unitPrice,
      currency: price?.currency ?? null,
      lineTotal: quantity * unitPrice,
      isUnknown: !material,
      hasPrice: price !== undefined,
    }
  })
  const subtotals = COST_TYPES.map((costType) => ({
    costType,
    amount: lines.filter((line) => line.costType === costType).reduce((sum, line) => sum + line.lineTotal, 0),
    present: lines.some((line) => line.costType === costType),
  }))
    .filter((item) => item.present)
    .map(({ costType, amount }) => ({ costType, amount }))
  const currencies = [
    ...new Set(lines.flatMap((line) => (line.currency && line.hasPrice ? [line.currency] : []))),
  ]
  return {
    lines,
    subtotals,
    total: lines.reduce((sum, line) => sum + line.lineTotal, 0),
    currencies,
  }
}
